import { Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { Fragment, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { bindActionCreators } from 'redux';
import Spinner from '/@/components/spinner';
import { useDefaultCropConfig } from '/@/hooks/recommendation';
import { useZones } from '/@/hooks/zones';
import { setField } from '/@/store/crop-monitoring/actions';
import { createFieldFromAPI } from '/@/store/fields/apiProviders';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { createRecommendationFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { setPreferences } from '/@/store/session/actions';
import { getAppInstance, getSessionCountries, getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { isMultiPolygon } from '/@/utils';
import { reverse } from '/@/utils/router';
import { seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import ErrorDialog from './error-dialog';
import OnlineFieldEditor from './online';

/** @param {typeof propTypes} props  */
function NewFieldV1(props) {
    const history = useNavigate();
    const dispatch = useDispatch();
    const zonesQuery = useZones({});
    const sessionCountry = useSelector((state) => getSessionCountry(state));
    const selectedFeature = useSelector(getSelectedFeature);
    const sessionCountries = useSelector((state) => getSessionCountries(state));
    const country = useSelector(getSessionCountry);
    const appInstance = useSelector((state) => getAppInstance(state));
    const actions = useMemo(
        () =>
            bindActionCreators(
                {
                    setPreferences,
                    setSelectedFeature,
                    setField,
                },
                dispatch
            ),
        [dispatch]
    );

    const [state, setState] = useState({
        error: undefined,
        field: undefined,
        creating: false,
    });
    const defaultCropConfig = useDefaultCropConfig({ crop: selectedFeature.crop || 'corn', field: state.field });

    const handleReset = () => {
        setState((prev) => ({ ...prev, error: undefined, field: undefined, creating: false }));
    };

    const handleError = ({ error, field }) => {
        setState((prev) => ({ ...prev, error, field }));
    };

    const handleCreateField = async (fieldData) => {
        // Normal creation
        setState((prev) => ({ ...prev, creating: true }));

        let { geometry } = fieldData;
        if (geometry.length > 1) {
            geometry = [...fieldData.geometry, fieldData.geometry[0]];
        }
        const { selectedFeature: innerSelectedFeature, crop } =
            (!Array.isArray(selectedFeature) && selectedFeature) || {};

        const parsedGeometry = isMultiPolygon(geometry) ? geometry[0] : geometry;
        const field = await createFieldFromAPI({
            name: fieldData.name as string,
            geometry: parsedGeometry.map((point) => ({ lat: point[1], lng: point[0] })),
            appInstance: appInstance.id as number,
            altitude: fieldData.altitude as number,
            area: fieldData.area as number,
            country: country.code as string,
        });
        mpTrackEvent('Field created', {
            area_hectares: fieldData?.area,
            field_id: field.id,
            creation_method: fieldData?.editMode,
        });

        const fieldId = field.id;

        if (innerSelectedFeature === 'weeds') {
            actions.setField(field);
            const data = { selectedFeature };
            actions.setSelectedFeature(data);

            const recommendation = await createRecommendationFromAPI({
                appInstance: appInstance.id,
                field: fieldId,
                weeds: [
                    {
                        crop: defaultCropConfig.crop,
                        defaultPlantingDate: defaultCropConfig.defaultPlantingDate,
                        plantingDate: defaultCropConfig.plantingDate,
                    },
                ],
            });

            return history(
                reverse('recommendations.RecommendationWeeds', { params: { id: recommendation.id, field: field.id } })
            );
        }

        if (innerSelectedFeature === 'h2h')
            return history(reverse('headToHead.HeadToHead', { params: { fieldId, crop } }));

        if (innerSelectedFeature === 'nitrogen')
            return history(reverse('nitrogenRecommendation.NitrogenRecommendationCreate', { params: { fieldId } }));

        if (innerSelectedFeature === 'recommendation') {
            return history(seedRecommendationUrl({ crop, fieldId, entrySource: 'new-field' }));
        }

        if (innerSelectedFeature === 'carbonCalculator') {
            return history(reverse('CarbonCalculator', { params: { fieldId } }));
        }

        if (innerSelectedFeature === 'cropMonitoring') {
            actions.setField(fieldId);
            return history(reverse('cropMonitoring.CropMonitoringStepperMobile'));
        }

        if (innerSelectedFeature === 'newNitrogenRecommendationForm') {
            actions.setField(fieldId);
            return history(reverse('nitrogenRecommendation.NitrogenRecommendationCreate', { params: { fieldId } }));
        }

        if (innerSelectedFeature === 'carbonCalculatorBR') {
            return history(reverse('CarbonCalculatorBR', { params: { fieldId } }));
        }

        if (innerSelectedFeature === 'productDatasheet') {
            return history(reverse('productDatasheet.ProductDatasheetBrandSelection', { params: { field } }));
        }

        actions.setSelectedFeature({ view: 'Home', selectedFeature: 'FieldDetail' });

        return history(reverse('Home', { qs: { selectedField: fieldId } }));
    };

    const handleDialogAccept = async () => {
        // Creation after error
        const { field, error } = state;
        if (error === 'country' && field) {
            await actions.setPreferences({ country: field.country });
            return handleCreateField(field);
        }
        handleReset();
    };

    return (
        <Fragment>
            {zonesQuery.isValidating ? (
                <Stack justifyContent='center' alignItems='center' height='100%'>
                    <Spinner />
                </Stack>
            ) : (
                <OnlineFieldEditor
                    {...props}
                    onError={handleError}
                    onCreateField={handleCreateField}
                    zones={zonesQuery.data}
                    sessionCountry={sessionCountry}
                    sessionCountries={sessionCountries}
                />
            )}
            <ErrorDialog
                type={state.error}
                visible={Boolean(state.error)}
                onAccept={handleDialogAccept}
                onCancel={handleReset}
                onClose={handleReset}
            />
        </Fragment>
    );
}

const propTypes = PropTypes.shape({});
NewFieldV1.propTypes = propTypes;

export default NewFieldV1;
